// @flow
import * as React from 'react';
import styled, {keyframes} from 'styled-components';
import ReactSlider from 'react-slider';
import ButtonIndex from 'components/ButtonIndex';
import ButtonNextPage from 'components/ButtonNextPage';
import {LayoutWrapper, useLayoutState} from 'components/Layout';
import {LocalizedParagraph, Paragraph, useLocalized} from 'components/Localized';
import Logo from 'components/Logo';
import Image, {BackgroundImage} from 'components/Image';
import Space from 'components/Space'
import GuideMessageContainer from 'components/GuideMessageContainer';
import GuideMessageCenter from 'components/GuideMessageCenter';
import {useSessionState, useSessionDispatch} from 'components/Session';
import SoundToggleButton from 'components/SoundToggleButton';
import ImgTimeGauge from 'assets/images/driving-zone/b/bar-charge-time-full.svg';
import * as VideoUtils from 'utils/videoUtils';
import Disclaimer from 'components/Disclaimer';
import * as AudioUtils from 'utils/audioUtils';

type Props = {
  onOpenModalIndex: () => void,
  openModalIndex: boolean,
  onNext: () => void,
  onSkip: () => void,
};

export default function ChargingStation(props: Props): React.Element<any> {
  const layoutState = useLayoutState();
  const localized = useLocalized();
  const [page, setPage] = React.useState(0);
  const sessionState = useSessionState();
  const sessionDispatch = useSessionDispatch();
  console.log("ChargingStation, page=", page);
  React.useEffect(()=>{
    AudioUtils.playBGM_UI2();
    return ()=>{
    };
  }, []);

  const handleNextPage = React.useCallback(() => {
    let nextPage = page + 1;
    if (nextPage === 1) {
      window.tagManager.chargingStationInfo1NextLinkClick();
    }
    if (nextPage === 2) {
      window.tagManager.chargingStationInfo2NextLinkClick();
    }

    if (nextPage === 2) {
      if (sessionState.skipGuide?.includes('charging-station')) {
        nextPage = 3;
      }
    }

    if (nextPage === 4) {
      window.tagManager.chargingStationGaugeNextLinkClick();
      sessionDispatch({type: 'SKIP_GUIDE', data: 'charging-station'});
      props.onNext();
    } else {
      setPage(nextPage);
    }
  }, [page, props, sessionState, sessionDispatch]);

  const [update, setUpdate] = React.useState(0)
  const point1 = React.useMemo(() => {
    const target = window.document.getElementById("charging-slider-thumb");
    return target?.getBoundingClientRect() ?? {};
    // eslint-disable-next-line
  }, [update])

  React.useEffect(() => {
    if (point1.x === undefined) {
      setTimeout(() => setUpdate(update+1), 100)
    }
  }, [update, point1])

  React.useEffect(() => {
    setUpdate(update + 1);
    setTimeout(() => setUpdate(update + 2));
    //eslint-disable-next-line
  }, [layoutState.width, layoutState.height])

  React.useEffect(()=>{
    VideoUtils.loadB01_1();
  }, []);

  const disclaimerKey = (page === 2 || page === 3) ?"disclaimer_B01" : "disclaimer_default";

  return (
    <main>
      {page >= 0 && page <= 2 && <Guide1 page={page} onNextPage={handleNextPage} />}
      <Guide2 page={page} onNextPage={handleNextPage} />
      <ButtonIndex onOpenModalIndex={props.onOpenModalIndex} />
      <ButtonNextPage title={localized.button_next} onNext={handleNextPage} />

      {page === 2 &&
      <GuideMessageContainer rootEvents={true} onClick={handleNextPage}>
        <GuideBack>
          <GuidePoint datarect={point1}>
            <Image source="./driving-zone/b/charging-guide-finger.png" style={{"pointerEvents":"none"}} />
          </GuidePoint>
        </GuideBack>
      </GuideMessageContainer>
      }
      <Logo />
      <SoundToggleButton />
      <Disclaimer textKey={disclaimerKey} />

    </main>
  );
}

type PropsGuide = {
  page: number,
  onNextPage: () => void,
};

function Guide1(props: PropsGuide): React.Element<any> {
  const localized = useLocalized();

  const handleNextPage = React.useCallback(() => {
    props.onNextPage();
  }, [props]);

  return (
    <GuideMessageContainer onClick={handleNextPage}>
      <GuideMessageCenter show={props.page === 0}>
        <LocalizedParagraph {...localized.driving_b01_charging_034} />
          <Space heightM={13} heightD={21} />
        <LocalizedParagraph {...localized.driving_b01_charging_031} />
      </GuideMessageCenter>
      <GuideMessageCenter show={props.page === 1}>
        <LocalizedParagraph {...localized.driving_b01_charging_032} />
      </GuideMessageCenter>
    </GuideMessageContainer>
  );
}

function Guide2(props: PropsGuide): React.Element<any> {
  const localized = useLocalized();
  const layoutState = useLayoutState();
  const {page} = props;
  const [gauge, setGauge] = React.useState(0);
  const [step, setStep] = React.useState(0);
  //console.log("Guide2, gauge:", gauge);

  const gaugeMarks = React.useMemo(() => layoutState.mobile ? [0, 17, 100] : [0, 28, 100], [layoutState])
  const gaugeBattery = React.useMemo(() => layoutState.mobile ? [15, 26, 96] : [14, 25, 96], [layoutState])
  const valueBattery = React.useMemo(() => (gauge >= gaugeMarks[2] ? gaugeBattery[2] : (gauge >= gaugeMarks[1] ? gaugeBattery[1] : gaugeBattery[0])) * 0.01, [gauge, gaugeMarks, gaugeBattery]);

  const handleNextPage = React.useCallback(() => {
    if (props.page >= 0 && props.page <= 3) {
      props.onNextPage();
    }
  }, [props]);

  const handleChangeStep = React.useCallback((value) => {
    setStep(value);
  }, []);

  const handleChangeGauge = React.useCallback((value) => {
    const target = gaugeMarks.reduce(function(prev, curr) {
      return (Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev);
    });

    setStep(target);
    setGauge(target);
  }, [gaugeMarks]);

  React.useEffect(()=>{
    if (page === 0) {
      window.tagManager.drivingZoneCourseBChargingStationInfo1Page();
    }
    if (page === 1) {
      window.tagManager.drivingZoneCourseBChargingStationInfo2Page();
    }
    if (page === 2) {
      window.tagManager.drivingZoneCourseBChargingStationGaugePage();
    }

  },[page]);


  const gaugeFiles = [
    "./driving-zone/b/img-b-charge-10-1.png",
    "./driving-zone/b/img-b-charge-20-1.png",
    "./driving-zone/b/img-b-charge-80.png"
  ];
  const gaugeIndex = React.useMemo(() => gauge >= gaugeMarks[2] ? 2 : (gauge >= gaugeMarks[1] ? 1 : 0), [gauge, gaugeMarks]);

  return (
    <Guide2Wrapper>
      <GuideMessageCenter show={props.page >= 2}>
        <LocalizedParagraph {...localized.driving_b01_charging_033} />
        <GuideFrame>
          {gaugeIndex === 0 && <LocalizedParagraph {...localized.driving_b01_charging_extra_001} />}
          {gaugeIndex === 1 && 
          <GuideFrameHeader>
            <GuideFrameHeaderCell>
              <div><LocalizedParagraph {...localized.driving_b01_charging_extra_002} /></div>
              <Paragraph textKey="driving_b01_charging_extra_011" />
            </GuideFrameHeaderCell>
            <Space widthM={40} widthD={90} />
            <GuideFrameHeaderCell>
              <div><LocalizedParagraph {...localized.driving_b01_charging_extra_003} /></div>
              <Paragraph textKey="driving_b01_charging_extra_012" />
            </GuideFrameHeaderCell>
          </GuideFrameHeader>}
          {gaugeIndex === 2 && 
          <GuideFrameHeader>
            <GuideFrameHeaderCell>
              <div><LocalizedParagraph {...localized.driving_b01_charging_extra_002} /></div>
              <Paragraph textKey="driving_b01_charging_extra_013" />
            </GuideFrameHeaderCell>
            <Space widthM={40} widthD={80} />
            <GuideFrameHeaderCell>
              <div><LocalizedParagraph {...localized.driving_b01_charging_extra_004} /></div>
              <Paragraph textKey="driving_b01_charging_extra_014" />
            </GuideFrameHeaderCell>
          </GuideFrameHeader>}
          <BackgroundImage source="./driving-zone/b/img-b-charge-car.png">
            <BatteryGauge source={gaugeFiles[gaugeIndex]} dataratio={valueBattery} />
            <BatteryGauge2 source={gauge >= gaugeMarks[2] ? "./driving-zone/b/img-b-charge-90.png" : "./driving-zone/b/img-b-charge-20-2.png"} dataratio={valueBattery} />
          </BackgroundImage>
          <div>
            <div>
              <LocalizedParagraph {...localized.driving_b01_charging_051} style={{color: gaugeIndex > 0 ? '#daff01' : '#ffffff'}}/>
            </div>
            <div>
              <LocalizedParagraph {...localized.driving_b01_charging_052} style={{color: gaugeIndex > 0 ? '#daff01' : '#ffffff', textAlign: "center"}} />
              <LocalizedParagraph {...localized.driving_b01_charging_053} style={{color: gaugeIndex > 0 ? '#daff01' : '#ffffff', textAlign: "center"} } />
            </div>
            <div>
              <LocalizedParagraph {...localized.driving_b01_charging_054} style={{color: gaugeIndex > 1 ? '#daff01' : '#ffffff', textAlign: "center"}} />
              <LocalizedParagraph {...localized.driving_b01_charging_055} style={{color: gaugeIndex > 1 ? '#daff01' : '#ffffff', textAlign: "center"}} />
            </div>
            <div>
              <LocalizedParagraph {...localized.driving_b01_charging_056} style={{color: gaugeIndex > 1 ? '#daff01' : '#ffffff', textAlign: "center"}} />
              <LocalizedParagraph {...localized.driving_b01_charging_057} style={{color: gaugeIndex > 1 ? '#daff01' : '#ffffff', textAlign: "center"}} />
            </div>
            <div>
              <LocalizedParagraph {...localized.driving_b01_charging_058} datatextalign="center" />
              <LocalizedParagraph {...localized.driving_b01_charging_059} datatextalign="center" />
            </div>
          </div>
          
          <TimeBar source="./driving-zone/b/bar-charge-time.png">
            <LocalizedParagraph {...localized.driving_b01_charging_061} />
            <LocalizedParagraph {...localized.driving_b01_charging_063} />
            <TimeGuage style={{width: step / 100 * (layoutState.mobile ? 301 : 525)}} />
            <BackgroundImage source="./driving-zone/b/bar-charge-time-1-5-sec.png">
              <LocalizedParagraph {...localized.driving_b01_charging_062} />
            </BackgroundImage>
            <StyledSlider
                value={step}
                //marks={[10, 20, 100]}
                //step={100}
                renderTrack={Track}
                renderThumb={(props, state) => <StyledThumb id="charging-slider-thumb" {...props}><Image source="./driving-zone/b/btn-b-charge-time.png" style={{visibility: page === 2 ? 'hidden' : 'visible'}} /></StyledThumb>}
                onChange={val => handleChangeStep(val)}
                onAfterChange={val => handleChangeGauge(val)}
            />
          </TimeBar>
        </GuideFrame>
      </GuideMessageCenter>
      {props.page >= 2 && (
        <ButtonNextPage title={localized.driving_b01_charging_044} onNext={handleNextPage} />
      )}
    </Guide2Wrapper>
  );
}

const Guide2Wrapper = styled.div`
  width: 100%;
  height: 100%;

  > div {
    display: flex;
    flex-direction: column;

    > p {
      margin: 13px 0px;
    }
  }
`;

const GuideFrame = styled.div`
  width: ${(props) => (props.theme.layout.mobile ? 448 : 818)}px;
  height: ${(props) => (props.theme.layout.mobile ? 221 : 444)}px;
  border: solid 1px #dcdcdc;
  background-color: rgba(0, 0, 0, 0.7);
  margin-bottom: ${(props) => (props.theme.layout.mobile ? 50 : 0)}px;
  flex-shrink: 0;

  > :nth-child(1) {
    line-height: ${(props) => (props.theme.layout.mobile ? 50 : 100)}px;
    text-align: center;
  }

  > :nth-child(2) {
    height: ${(props) => (props.theme.layout.mobile ? 78 : 155)}px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center center;
    margin-right: ${(props) => (props.theme.layout.mobile ? 14 : 28)}px;
    margin-top: ${(props) => (props.theme.layout.mobile ? 8 : 14)}px;
    margin-bottom: ${(props) => (props.theme.layout.mobile ? 22 : 45)}px;
    position: relative;

    p {
      text-align: center;
      padding-top: ${(props) => (props.theme.layout.mobile ? 10 : 25)}px;
    }
  }

  > :nth-child(3) {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 0px ${(props) => (props.theme.layout.mobile ? 34 : 62)}px;
  }

  > :nth-child(4) {
    > * {
    pointer-events: auto;
    }
  }
`;

const GuideFrameHeader = styled.div`
  width: calc(100% - ${(props) => (props.theme.layout.mobile ? 50 * 2 : 83 * 2)}px);
  height: ${(props) => (props.theme.layout.mobile ? 42 : 84)}px;

  display: flex;
  flex-direction: row;
  padding: ${(props) => (props.theme.layout.mobile ? 4 : 8)}px ${(props) => (props.theme.layout.mobile ? 50 : 83)}px;
`;

const GuideFrameHeaderCell = styled.div`
  flex: 1;

  display: flex;
  flex-direction: row;
  align-items: center;

  padding-top: ${(props) => (props.theme.layout.mobile ? 15 : 24)}px;
  padding-bottom: ${(props) => (props.theme.layout.mobile ? 5 : 10)}px;
  padding-left: ${(props) => (props.theme.layout.mobile ? 5 : 10)}px;
  padding-right: ${(props) => (props.theme.layout.mobile ? 5 : 10)}px;

  border-bottom: solid 1px #dcdcdc;
  vertical-align: middle;

  >div {
    flex: 1;
  }

  > img {
    vertical-align: middle;
  }

  > p {
    vertical-align: middle;
  }
`;

const BatteryGauge = styled(Image)`
  transform: ${(props) => (props.theme.layout.mobile ? "scale(0.5)" : "")};
  transform-origin: 0% 0%;
  position: absolute;
  left: ${(props) => (props.theme.layout.mobile ? 186 : 334)}px;
  top: ${(props) => (props.theme.layout.mobile ? 37 : 73)}px;
`;

const fadeInOut = keyframes`
  from {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
  to {
    opacity: 1;
  }
`;

const BatteryGauge2 = styled(BackgroundImage)`
  background-size: cover;
  background-repeat: no-repeat;
  visibility: ${props => props.dataratio >= 0.1 ? 'visible' : 'hidden'};

  position: absolute;
  left: ${(props) =>
    (props.theme.layout.mobile ? 186 : 334) -
    (props.theme.layout.mobile ? 3 : 7) +
    props.dataratio * (props.theme.layout.mobile ? 63 : 125)}px;
  top: ${(props) => (props.theme.layout.mobile ? 37 : 73)}px;
  width: ${(props) => (props.theme.layout.mobile ? 10 : 20)}px;
  height: ${(props) => (props.theme.layout.mobile ? 24 : 47)}px;

  animation: 1.2s ease-in-out 1s ${fadeInOut};
  animation-iteration-count: infinite;
`;
const TimeBar = styled(BackgroundImage)`
  width: ${(props) => (props.theme.layout.mobile ? 388 : 696)}px;
  height: ${(props) => (props.theme.layout.mobile ? 11 : 18)}px;
  margin: 0px ${(props) => (props.theme.layout.mobile ? 30 : 62)}px;
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: center center;
  margin-top: ${(props) => (props.theme.layout.mobile ? 14 : 34)}px;
  
  position: relative;
  background-size: cover;
  background-repeat: no-repeat;
  > p {
    line-height: ${(props) => (props.theme.layout.mobile ? 11 : 18)}px;
  }
  > :nth-child(1) {
    position: absolute;
    left: ${(props) => (props.theme.layout.mobile ? 19 : 31)}px;
    top: ${(props) => (props.theme.layout.mobile ? 1 : 0)}px;
  }
  > :nth-child(2) {
    position: absolute;
    left: ${(props) => (props.theme.layout.mobile ? 349 : 630)}px;
    top: ${(props) => (props.theme.layout.mobile ? 1 : 0)}px;
  }
  > :nth-child(4) {
    position: absolute;
    left: ${(props) => (props.theme.layout.mobile ? 94 : 221)}px;
    top : 0px;
    width: ${(props) => (props.theme.layout.mobile ? 77 : 123)}px;
    height: ${(props) => (props.theme.layout.mobile ? 11 : 18)}px;

    > p {
      position: relative;
      text-align: right;
      left : ${(props) => (props.theme.layout.mobile ? -25 : -42)}px;
      padding-left: ${(props) => (props.theme.layout.mobile ? 0 : 0)}px;
      padding-top: ${(props) => (props.theme.layout.mobile ? 1 : 2)}px;
    }
  }
  > :nth-child(5) {
    position: absolute;
  }
`;
const TimeGuage = styled.div`
  background-image: url(${ImgTimeGauge});
  background-size: cover;
  background-repeat: no-repeat;

  position: absolute;
  left: ${(props) => (props.theme.layout.mobile ? 43 : 72)}px;
  top: ${(props) => (props.theme.layout.mobile ? 1 : 2.5)}px;
  height: ${(props) => (props.theme.layout.mobile ? 9 : 14)}px;
`;

const StyledSlider = styled(ReactSlider)`
  width: calc(100% - ${(props) => (props.theme.layout.mobile ? 40 * 2 : 66 + 64)}px);
  height: ${(props) => (props.theme.layout.mobile ? 11 : 18)}px;
  margin-left: ${(props) => (props.theme.layout.mobile ? 40 : 66)}px;
  margin-right: ${(props) => (props.theme.layout.mobile ? 40 : 64)}px;
`;

const StyledThumb = styled.div`
  position: relative;
  top: ${(props) => (props.theme.layout.mobile ? -12 : -20)}px;
  width: ${(props) => (props.theme.layout.mobile ? 37 : 64)}px;
  height: ${(props) => (props.theme.layout.mobile ? 38 : 65)}px;
  outline:none;
  cursor: grab;

  img {
    width: 100%;
    height: 100%;
  }
`;

const StyledTrack = styled.div`
  top: 0;
  bottom: 0;
  //background: ${props => props.index === 2 ? '#f00' : props.index === 1 ? '#0f0' : '#ddd'};
`;

const Track = (props, state) => <StyledTrack {...props} index={state.index} />;

const GuideBack = styled(LayoutWrapper)`
  background-color: rgba(0, 0, 0, 0.6);
`;

const leftRight = keyframes`
  0% {
    transform: translateX(-10px)
  }
  50% {
    transform: translateX(10px)
  }
  100% {
    transform: translateX(-10px)
  }
`;

const GuidePoint = styled.div`
  position: absolute;
  left: ${props => props.datarect.x + (props.datarect.width * 0.5) - (props.theme.layout.mobile ? 108 * 0.5 : 174 * 0.5)}px;
  top: ${props => props.datarect.y}px;
  > img {
    width: ${props => props.theme.layout.mobile ? 108 : 174}px;
    height: ${props => props.theme.layout.mobile ? 87 : 140}px;
  }

  animation-name: ${leftRight};
  animation-duration: 2s;
  animation-iteration-count: infinite;
`;
